import { useEffect, useRef, useState, type CSSProperties, type KeyboardEvent } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Ic from "../icons.jsx";
import { useAssistantChat } from "./useAssistantChat";

/**
 * Global Bankom AI chat widget: a fixed FAB bottom-right that toggles a discreet
 * panel. Mounted once in AppShell (authenticated shell only). No React portal —
 * fixed positioning + zIndex, matching SalesRepPopup / NotificationBell.
 */
export function AssistantChat() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { open, setOpen, messages, pending, send } = useAssistantChat();
  const [draft, setDraft] = useState("");
  const panelRef = useRef<HTMLDivElement | null>(null);
  const fabRef = useRef<HTMLButtonElement | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);

  // Click-outside closes the panel (mousedown, like NotificationBell). The FAB
  // is excluded so its own toggle isn't cancelled by this listener.
  useEffect(() => {
    if (!open) return;
    function onClick(e: MouseEvent) {
      const target = e.target as Node;
      if (panelRef.current?.contains(target)) return;
      if (fabRef.current?.contains(target)) return;
      setOpen(false);
    }
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open, setOpen]);

  // Keep the newest content in view as tokens stream in.
  useEffect(() => {
    if (!open) return;
    const el = listRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, pending, open]);

  function submit() {
    if (!draft.trim() || pending) return;
    send(draft);
    setDraft("");
  }

  function onKeyDown(e: KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  }

  const last = messages.length ? messages[messages.length - 1] : null;
  const showThinking = pending && last?.role === "assistant" && !last.content;

  const suggestions = [
    t("assistant.suggest.orders", "My recent orders"),
    t("assistant.suggest.catalog", "Find BIOPRO products"),
  ];

  return (
    <>
      {open && (
        <div
          ref={panelRef}
          className="ai-chat-panel"
          style={panelStyle}
          role="dialog"
          aria-label={t("assistant.title", "Bankom AI")}
        >
          <div className="ai-chat-head" style={headStyle}>
            <span className="ai-chat-head-icon" style={headIconStyle}>
              <Ic.chat />
            </span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 700, fontSize: 15.5, color: "var(--on-primary)", letterSpacing: "-0.01em" }}>
                {t("assistant.title", "Bankom AI")}
              </div>
              <div className="ai-chat-subtitle">{t("assistant.subtitle", "Answers from your portal data")}</div>
            </div>
            <button
              type="button"
              className="ai-chat-x"
              onClick={() => setOpen(false)}
              aria-label={t("assistant.close", "Close")}
              style={closeBtnStyle}
            >
              ×
            </button>
          </div>

          <div ref={listRef} className="ai-chat-body dd-scroll" style={bodyStyle}>
            {messages.length === 0 && (
              <div className="ai-chat-welcome">
                <div className="ai-chat-greeting">
                  {t(
                    "assistant.greeting",
                    "Hi! I can help within your portal data. Ask me about your orders, catalog, or materials.",
                  )}
                </div>
                <div className="ai-suggest">
                  {suggestions.map((s, i) => (
                    <button
                      key={`sg-${i}`}
                      type="button"
                      className="ai-suggest-chip"
                      onClick={() => {
                        if (!pending) send(s);
                      }}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {messages.map((m) => (
              <div key={m.id} className={`ai-msg ai-msg-${m.role}`}>
                {m.content && <div className={`ai-bubble ai-bubble-${m.role}`}>{m.content}</div>}
                {m.role === "assistant" && m.cards && m.cards.length > 0 && (
                  <div className="ai-cards">
                    {m.cards.map((c, i) => (
                      <button
                        key={`${m.id}-c${i}`}
                        type="button"
                        className="ai-card"
                        onClick={() => navigate(c.href)}
                      >
                        <span className="ai-card-title">{c.title}</span>
                        {c.subtitle && <span className="ai-card-sub">{c.subtitle}</span>}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}

            {showThinking && (
              <div className="ai-msg ai-msg-assistant">
                <div className="ai-thinking">
                  <span className="oi-spinner" aria-hidden />
                  <span className="ai-thinking-label">{t("assistant.thinking", "Bankom AI is thinking…")}</span>
                </div>
              </div>
            )}
          </div>

          <div style={footStyle}>
            <textarea
              className="ai-chat-input"
              rows={1}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder={t("assistant.placeholder", "Ask about your orders, catalog…")}
              style={inputStyle}
            />
            <button
              type="button"
              className="ai-chat-send"
              onClick={submit}
              disabled={pending || !draft.trim()}
              aria-label={t("assistant.send", "Send")}
              title={t("assistant.send", "Send")}
              style={sendStyle}
            >
              <Ic.send />
            </button>
          </div>
        </div>
      )}

      <button
        ref={fabRef}
        type="button"
        className="ai-chat-fab"
        onClick={() => setOpen(!open)}
        aria-label={t("assistant.launcher.tooltip", "Ask Bankom AI")}
        title={t("assistant.launcher.tooltip", "Ask Bankom AI")}
        style={fabStyle}
      >
        {open ? <Ic.x /> : <Ic.chat />}
      </button>
    </>
  );
}

const fabStyle: CSSProperties = {
  position: "fixed",
  right: 22,
  bottom: 22,
  zIndex: 60,
  width: 58,
  height: 58,
  borderRadius: "50%",
  border: "none",
  cursor: "pointer",
  color: "var(--on-primary)",
  display: "grid",
  placeItems: "center",
  background: "linear-gradient(135deg, var(--brand-primary), var(--brand-ink))",
  boxShadow: "var(--shadow-2)",
};
const panelStyle: CSSProperties = {
  position: "fixed",
  right: 22,
  bottom: 92,
  zIndex: 60,
  width: 448,
  maxWidth: "calc(100vw - 32px)",
  height: "min(660px, calc(82vh / var(--text-zoom)))",
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
  background: "var(--surface)",
  border: "1px solid var(--border)",
  borderRadius: 18,
  boxShadow: "var(--shadow-2)",
};
const headStyle: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 12,
  padding: "16px 18px",
  flex: "none",
  background: "linear-gradient(135deg, var(--brand-primary), var(--brand-ink))",
};
const headIconStyle: CSSProperties = {
  flex: "none",
  width: 34,
  height: 34,
  borderRadius: 10,
  display: "grid",
  placeItems: "center",
  color: "var(--on-primary)",
  background: "color-mix(in srgb, var(--on-primary) 16%, transparent)",
};
const closeBtnStyle: CSSProperties = {
  flex: "none",
  width: 30,
  height: 30,
  borderRadius: 9,
  border: "none",
  cursor: "pointer",
  background: "color-mix(in srgb, var(--on-primary) 16%, transparent)",
  color: "var(--on-primary)",
  fontSize: 19,
  lineHeight: 1,
  display: "grid",
  placeItems: "center",
};
const bodyStyle: CSSProperties = {
  flex: 1,
  minHeight: 0,
  overflowY: "auto",
  padding: "18px 18px 8px",
  display: "flex",
  flexDirection: "column",
  gap: 14,
};
const footStyle: CSSProperties = {
  flex: "none",
  display: "flex",
  alignItems: "flex-end",
  gap: 10,
  padding: "12px 16px 16px",
  borderTop: "1px solid var(--border)",
};
const inputStyle: CSSProperties = {
  flex: 1,
  resize: "none",
  maxHeight: 132,
  padding: "11px 13px",
  fontSize: 16, // 16px avoids iOS Safari's zoom-on-focus for the chat input
  fontFamily: "inherit",
  lineHeight: 1.45,
  color: "var(--ink)",
  background: "var(--surface-2)",
  border: "1px solid var(--border)",
  borderRadius: 12,
  outline: "none",
};
const sendStyle: CSSProperties = {
  flex: "none",
  width: 42,
  height: 42,
  borderRadius: 12,
  border: "none",
  cursor: "pointer",
  color: "var(--on-primary)",
  background: "var(--brand-primary)",
  display: "grid",
  placeItems: "center",
};
